/**
 * SourceSearchResults — two-column document search inside the Knowledge Base.
 *
 * Renders below the existing curated knowledge entries when the user types a
 * query. Searches **Epicor attachments** (left column) and **Outlook emails**
 * (right column) in parallel. Each result is a card; clicking a card opens a
 * full-screen modal viewer that reuses the same SourceRenderer used by the
 * chat's right pane.
 *
 * This is intentionally a smaller scope than the plan's "matching documents +
 * agent's structured extracts" two-column layout — the agent-extracts column
 * required the full agent in the loop and was deferred. The documents +
 * emails surface still satisfies the user-facing goal: "search across what
 * Trendy can actually see, not just what's in the curated KB."
 */

import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { FileText, Mail, Loader2, Search as SearchIcon, X } from 'lucide-react'
import { executeTool } from '../../lib/api'
import { SourcePaneProvider } from '../../contexts/SourcePaneContext'

const SourceRenderer = lazy(() => import('../chat/SourceRenderer'))

const fmtDate = (s) => {
  if (!s) return ''
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function SourceSearchResults({ query }) {
  const trimmed = (query || '').trim()
  const [attachments, setAttachments] = useState(null) // null = not fetched
  const [emails, setEmails] = useState(null)
  const [errA, setErrA] = useState(null)
  const [errE, setErrE] = useState(null)
  const [loadingA, setLoadingA] = useState(false)
  const [loadingE, setLoadingE] = useState(false)
  const [viewing, setViewing] = useState(null) // a Citation-shaped object

  useEffect(() => {
    if (!trimmed) {
      setAttachments(null); setEmails(null); setErrA(null); setErrE(null)
      return
    }
    let cancelled = false
    setLoadingA(true); setLoadingE(true); setErrA(null); setErrE(null)

    executeTool('search_attachments', { filename_contains: trimmed, limit: 20 })
      .then(resp => {
        if (cancelled) return
        const r = (resp && resp.result) || resp || {}
        // Tool response shape can be either { attachments: [...] } or { results: [...] }
        const list = r.attachments || r.results || []
        setAttachments(Array.isArray(list) ? list : [])
      })
      .catch(e => { if (!cancelled) setErrA(String(e?.message || e)) })
      .finally(() => { if (!cancelled) setLoadingA(false) })

    executeTool('search_outlook_emails', { query: trimmed, limit: 15 })
      .then(resp => {
        if (cancelled) return
        const r = (resp && resp.result) || resp || {}
        const list = r.emails || []
        setEmails(Array.isArray(list) ? list : [])
      })
      .catch(e => { if (!cancelled) setErrE(String(e?.message || e)) })
      .finally(() => { if (!cancelled) setLoadingE(false) })

    return () => { cancelled = true }
  }, [trimmed])

  if (!trimmed) return null

  return (
    <SourcePaneProvider>
      <section className="mt-6 mb-10">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
          <SearchIcon size={14} className="text-gray-400" />
          Search results across Trendy&apos;s sources
          <span className="text-xs font-normal text-gray-400">— &ldquo;{trimmed}&rdquo;</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Column
            title="Epicor attachments"
            icon={<FileText size={12} />}
            loading={loadingA}
            error={errA}
            items={attachments}
            empty="No attachments match this query."
            renderItem={(att) => <AttachmentCard att={att} onOpen={setViewing} />}
          />
          <Column
            title="Outlook emails"
            icon={<Mail size={12} />}
            loading={loadingE}
            error={errE}
            items={emails}
            empty="No emails match this query."
            renderItem={(em) => <EmailCard email={em} onOpen={setViewing} />}
          />
        </div>
      </section>

      {viewing && (
        <SourceModal citation={viewing} onClose={() => setViewing(null)} />
      )}
    </SourcePaneProvider>
  )
}

// ── primitives ──────────────────────────────────────────────────────────────

function Column({ title, icon, loading, error, items, empty, renderItem }) {
  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
      <header className="px-3 py-2 bg-gray-50 dark:bg-surface-dark-raised border-b border-gray-200 dark:border-gray-800 flex items-center gap-2">
        <span className="text-gray-400">{icon}</span>
        <h3 className="text-[11px] uppercase tracking-wide font-semibold text-gray-600 dark:text-gray-300">{title}</h3>
        {loading && <Loader2 size={12} className="animate-spin text-gray-400 ml-auto" />}
        {!loading && items && (
          <span className="ml-auto text-[10px] text-gray-400 tabular-nums">{items.length}</span>
        )}
      </header>
      <div className="divide-y divide-gray-100 dark:divide-gray-900">
        {error ? (
          <div className="p-3 text-xs text-red-600 dark:text-red-400">{error}</div>
        ) : !items && loading ? (
          <div className="p-3 text-xs text-gray-400">Searching…</div>
        ) : items && items.length === 0 ? (
          <div className="p-3 text-xs text-gray-400">{empty}</div>
        ) : items ? (
          items.map((it, i) => <div key={i}>{renderItem(it)}</div>)
        ) : null}
      </div>
    </div>
  )
}

function AttachmentCard({ att, onOpen }) {
  // Normalize from either GetRows shape or the formatted shape used elsewhere
  const xref = att.xfile_ref_num || att.XFileRefNum
  const filename = att.filename || att.XFileRefXFileName || att.file_name || ''
  const docType = att.doc_type_id || att.XFileRefDocTypeID || ''
  const description = att.description || att.XFileRefXFileDesc || ''
  const previewable = att.can_preview !== false &&
    (att.is_pdf === true || filename.toLowerCase().endsWith('.pdf') || att.is_image === true)
  return (
    <button
      type="button"
      onClick={previewable && xref ? () => onOpen({
        id: `epicor_attach_${xref}`,
        label: filename,
        kind: docType === 'PARTDRAW' ? 'shop_drawing' : docType === 'SPECS' ? 'spec_sheet' : 'epicor_pdf',
        payload: {
          xfile_ref_num: xref,
          filename,
          description,
          doc_type: docType,
          url: `/api/attachments/file/${xref}`,
        },
      }) : undefined}
      disabled={!previewable || !xref}
      className={
        'w-full text-left px-3 py-2 transition-colors ' +
        (previewable && xref
          ? 'hover:bg-gray-50 dark:hover:bg-surface-dark-raised'
          : 'opacity-60 cursor-not-allowed')
      }
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-gray-800 dark:text-gray-200 truncate min-w-0 flex-1">{filename || '(unnamed)'}</span>
        {docType && (
          <span className="text-[9px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400 flex-shrink-0">
            {docType}
          </span>
        )}
      </div>
      {description && (
        <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{description}</div>
      )}
    </button>
  )
}

function EmailCard({ email, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen({
        id: `email_${email.id || email.subject}`,
        label: email.subject || '(no subject)',
        kind: 'email',
        // search_outlook_emails returns a list with previews; we pass the
        // partial shape and EmailRenderer will render best-effort. A full
        // fetch via get_email_detail would happen if the user wants the body,
        // but for PR3 the preview-shaped render is acceptable.
        payload: {
          id: email.id,
          subject: email.subject,
          from: email.from_name ? { name: email.from_name, address: email.from } : email.from,
          received: email.received,
          body: email.preview || '',
          body_type: 'Text',
          has_attachments: email.has_attachments,
        },
      })}
      className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-surface-dark-raised transition-colors"
    >
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <span className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate min-w-0 flex-1">{email.subject || '(no subject)'}</span>
        <span className="text-[10px] text-gray-400 whitespace-nowrap">{fmtDate(email.received)}</span>
      </div>
      <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
        {(email.from_name || email.from || '') + (email.preview ? ` — ${email.preview}` : '')}
      </div>
    </button>
  )
}

function SourceModal({ citation, onClose }) {
  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl h-[80vh] bg-white dark:bg-surface-dark rounded-lg shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-800">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{citation.label}</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-surface-dark-overlay"
          >
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-hidden">
          <Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading viewer…</div>}>
            <SourceRenderer citation={citation} />
          </Suspense>
        </div>
      </div>
    </div>
  )
}
